/**
 * Sentry's environment tag and sample rates, resolved from the **runtime** environment name.
 *
 * `entry.client.tsx` used to derive both from `import.meta.env.MODE` / `import.meta.env.PROD`,
 * which are baked in at *build* time. The frontend image is built once and promoted (dev →
 * staging → production), so those constants are always `"production"`/`true` in a deployed
 * build: every dev and staging session was reporting itself as production, and the `PROD ? …`
 * branch for non-prod sampling could only ever be reached by a local `npm run dev`.
 *
 * `VITE_ENVIRONMENT` is the runtime value (per-environment k8s ConfigMap, exposed to the client
 * through `window._env_` — see getRuntimeEnv), so it is the one that can actually tell the three
 * deployments apart.
 *
 * Rates: Sentry bills tracing per span, and one kept pageload trace fans out into a child span
 * per resource/fetch request. Non-prod deployments only need enough traces to spot-check latency
 * (errors are never sampled away by `tracesSampleRate`), so they run at 5% against production's
 * 10%. Session replay is likewise production-only — the org's replay quota is tiny and dev
 * sessions were consuming it under a "production" label; `replaysOnErrorSampleRate` is left to
 * the caller so replays still attach to real errors everywhere.
 */
export const PRODUCTION_ENVIRONMENT = "production";

export const PRODUCTION_TRACES_SAMPLE_RATE = 0.1;
export const NON_PRODUCTION_TRACES_SAMPLE_RATE = 0.05;

export const PRODUCTION_REPLAYS_SESSION_SAMPLE_RATE = 0.1;
export const NON_PRODUCTION_REPLAYS_SESSION_SAMPLE_RATE = 0;

export type SentryEnvironmentConfig = {
  environment: string;
  enabled: boolean;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
};

/**
 * @param environment Runtime environment name (`VITE_ENVIRONMENT`), falling back to the build
 *   mode. Blank/unknown values are treated as non-production: under-sampling an unlabelled
 *   deployment costs a few traces, over-sampling one costs quota.
 * @param isLocalDevBuild `import.meta.env.DEV` — true only for a local `npm run dev` server
 *   (deployed images are production builds). Local dev sessions report HMR/Fast Refresh
 *   artifacts — context lost on refresh, mid-refactor states — that read as high-priority
 *   production-shaped errors in Sentry, so reporting is disabled for them entirely.
 */
export const resolveSentryEnvironmentConfig = (
  environment: string | null | undefined,
  { isLocalDevBuild = false }: { isLocalDevBuild?: boolean } = {},
): SentryEnvironmentConfig => {
  const resolved = environment?.trim() || "unknown";
  const isProduction = resolved === PRODUCTION_ENVIRONMENT;

  return {
    environment: resolved,
    enabled: !isLocalDevBuild,
    tracesSampleRate: isProduction
      ? PRODUCTION_TRACES_SAMPLE_RATE
      : NON_PRODUCTION_TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: isProduction
      ? PRODUCTION_REPLAYS_SESSION_SAMPLE_RATE
      : NON_PRODUCTION_REPLAYS_SESSION_SAMPLE_RATE,
  };
};
